import axios from 'axios';
import { supabase } from '../../lib/supabase';

// Booking module — wraps Supabase RPC responses in the legacy
// `{ data, error, message }` envelope the Express API returned so call sites
// like `setKpis(resp?.data ?? null)` keep working without changes.
// See docs/booking-module-supabase-migration-issues.md for which helpers
// are migrated vs. still pointing at Cloud Run.
const wrapSupabase = (data) => ({ error: false, data, message: 'ok' });
// `label` is an optional human-readable descriptor of the failing call (the RPC
// name, or a phrase like 'playbook detail'). The feature-level rpc helpers
// (ga4Rpc/eiRpc/ciRpc/cdpRpc/riRpc) all forward one; without it every failure
// surfaced as a bare "Supabase RPC failed" with no clue which call broke.
const wrapSupabaseError = (err, label) => {
  const detail = err?.message || 'Supabase RPC failed';
  const message = label ? `${label}: ${detail}` : detail;
  const error = new Error(message);
  error.response = { data: { error: true, message, data: null } };
  throw error;
};

// Default retry schedule for AIM/RI — the cold plan blows the timeout, the
// warm plan (cached on the very next call) does not. Tight delays are fine
// because only 1-2 RPCs fire per page there.
const RPC_RETRY_DELAYS_MS = [250, 750];

const isStatementTimeout = (err) =>
  err?.code === STATEMENT_TIMEOUT_CODE
  || /statement timeout/i.test(err?.message || '');

// 2026-09-04 — transport-level failures (HTTP/2 stream refused, connection
// reset by peer, "failed to fetch", "Load failed") surface as an error with
// NO Postgres `.code`. On a cold cache the CDP RPCs are slow enough to trip
// Supabase's edge/connection-pooler timeout BEFORE Postgres' statement_timeout
// (57014) fires, so the 57014-only retry never engaged — the error surfaced,
// the page caught it and set KPIs to null, and Visitor Intelligence rendered
// all zeros. Retrying these transport failures (the same recovery gscRpc uses
// for Search Console) lets the cold load self-recover the same way a manual
// date-picker toggle does: by attempt 2 the cache is warm and the connection
// is stable. Safe for reads only — same constraint as the 57014 retry.
//
// NB: a generic "network unreachable" deliberately does NOT match — it has no
// code but is not a transient stream/pooler failure, so it stays a hard fail
// (the established RI/AIM boundary).
const TRANSPORT_FAILURE = /failed to fetch|networkerror|load failed|err_http2|connection (closed|reset)|aborted/i;
const isTransportFailure = (err) =>
  !err?.code && TRANSPORT_FAILURE.test(err?.message || '');

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryable = (err) => !!err && (isStatementTimeout(err) || isTransportFailure(err));

/**
 * Calls a read-only Supabase RPC, retrying when Postgres aborted the statement
 * on timeout (57014) OR when the call failed at the transport layer (HTTP/2
 * stream refused, connection reset, "failed to fetch"). Non-retryable errors
 * and successes return immediately.
 *
 * Transport failures are retried because on a cold cache a slow RPC can trip
 * Supabase's edge/connection-pooler timeout before Postgres' statement_timeout
 * — the error carries no Postgres `.code`, so without this retry it surfaces
 * immediately and the calling page renders all zeros (Visitor Intelligence's
 * cold-load symptom). By the retry the cache is warm and the connection stable,
 * the same recovery a manual date-picker toggle triggers.
 *
 * Returns Supabase's `{ data, error }` verbatim so callers keep the error
 * handling they already have. Safe for reads only — a timeout or transport
 * failure on a write is ambiguous about whether it committed, so write call
 * sites must opt out.
 *
 * The call is wrapped in try/catch, not a bare `await`: supabase.rpc() returns
 * a thenable PostgrestFilterBuilder whose rejection on a transport failure
 * throws from `await` (it has `then` but no `catch`), which would bypass the
 * retry loop and surface as an unhandled throw. Catching it normalises the
 * throw into a `{ data: null, error }` result the loop can inspect — the same
 * shape gscRpc already uses.
 *
 * @param {string} name RPC function name.
 * @param {object} params RPC arguments.
 * @param {number[]} [delays=RPC_RETRY_DELAYS_MS] Retry delay schedule in ms.
 *   One retry per entry; the last attempt is the one whose result is returned
 *   if all retries fail.
 * @returns {Promise<{data: any, error: any}>} The last attempt's result.
 */
export async function rpcReadWithRetry(name, params, delays = RPC_RETRY_DELAYS_MS) {
  const attempt = async () => {
    try {
      return await supabase.rpc(name, params);
    } catch (e) {
      return { data: null, error: e };
    }
  };
  let result = await attempt();
  for (const ms of delays) {
    if (!isRetryable(result.error)) return result;
    await sleep(ms);
    result = await attempt();
  }
  return result;
}

// Helper function to clear auth data and redirect to login
const handleAuthExpired = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('username');
  localStorage.removeItem('role');
  localStorage.removeItem('client_id');
  localStorage.removeItem('user_id');
  localStorage.removeItem('rememberMe');
  localStorage.removeItem('avatar');
  localStorage.removeItem('theme_preference');
  
  // Only redirect if not already on login page
  if (window.location.pathname !== '/') {
    window.location.href = '/';
  }
};

// Axios response interceptor to handle expired tokens globally
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      const isLoginRequest = error.config?.url?.includes('/login');
      if (!isLoginRequest) {
        handleAuthExpired();
      }
    }
    return Promise.reject(error);
  }
);
// =====================================================
// WORK APIs
// =====================================================

// ─── Reads ──────────────────────────────────────────────────────────────────────

export const getWorkList = async ({
  client_id,
  bucket,
  work_type,
  owner_id,
  service,
  search,
  include_done = false,
  group_by = "bucket",
  limit = 200,
  include_subtasks = false,
} = {}) => {
  // Returns { counts, total, group_by, groups[{ key, count, items[] }] }.
  // The bucket is computed by fn_work_bucket — never derived client-side.
  const params = { p_limit: limit, p_include_done: include_done, p_group_by: group_by, p_include_subtasks: include_subtasks };
  if (client_id)   params.p_client_id = client_id;
  if (bucket)      params.p_bucket = bucket;
  if (work_type)   params.p_work_type = work_type;
  if (owner_id)    params.p_owner_id = owner_id;
  if (service)      params.p_service = service;
  if (search)      params.p_search = search;
  const { data, error } = await supabase.rpc("fn_work_list", params);
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getWorkDetail = async (taskId) => {
  // Returns { task, positions[], thread_id, messages[], checklists[],
  //   dependencies[], attachments[], commitments[], effort, past_similar }.
  const { data, error } = await supabase.rpc("fn_work_detail", {
    p_task_id: taskId,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getWorkSubtasks = async (taskId) => {
  // Returns subtasks for a parent task. Shown on expand — hidden by default.
  const { data, error } = await supabase.rpc("fn_work_subtasks", {
    p_task_id: taskId,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getWorkSearch = async (query, limit = 40) => {
  // Free-text search across work items.
  const { data, error } = await supabase.rpc("fn_work_search", {
    p_q: query,
    p_limit: limit,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getWorkLoad = async (clientId, { owner_id, days = 21, everyone = false } = {}) => {
  // Returns { total_open, ... } — work load across the team or one person.
  const params = { p_days: days, p_everyone: everyone };
  if (client_id) params.p_client_id = clientId;
  if (owner_id)  params.p_owner_id = owner_id;
  const { data, error } = await supabase.rpc("fn_work_load", params);
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

// ops_work_types — the source of truth for the pipeline/work-type dropdown.
// Table columns: work_type, label, statuses, ... (no `name` column — ordering by
// `name` caused the 400 Bad Request). Returns rows ordered by `label`. Wraps the
// PostgREST {data,error} in the same {data,error,message} envelope as the RPC
// helpers so call sites read `resp.data`; on error it throws so the caller's
// .catch can fall back to a hardcoded list.
export const getWorkTypes = async () => {
  const { data, error } = await supabase.from("ops_work_types").select("*").order("label");
  if (error) wrapSupabaseError(error, "ops_work_types");
  return wrapSupabase(data);
};

// Per-work-type status vocabulary, read from ops_work_types.statuses — a jsonb
// array PER WORK TYPE (nine work types, each its own list; verified live). The
// "Move work" menu (D4 / §A-1) reads this so it never hardcodes a status list
// (KB §16). Cached per work type for the session so the menu never re-fetches
// the list it already has. Reuses getWorkTypes' select('*') (which already
// carries `statuses`) and finds the row by `code` (the key the app uses) with a
// `work_type` fallback for safety.
const workTypeStatusCache = new Map();
export const getWorkTypeStatuses = async (workType) => {
  if (!workType) return [];
  if (workTypeStatusCache.has(workType)) return workTypeStatusCache.get(workType);
  let statuses = [];
  try {
    const resp = await getWorkTypes();
    const rows = resp?.data || [];
    const row = rows.find((r) => r.code === workType || r.work_type === workType);
    statuses = Array.isArray(row?.statuses) ? row.statuses : [];
    // Cache only a real read — a failure stays uncached so a retry can succeed.
    workTypeStatusCache.set(workType, statuses);
  } catch (e) {
    statuses = [];
  }
  return statuses;
};

// fn_forecast_task — the full forecast picture for one task. Returns
// { times_typical: { median, ... }, past_p90, observations, basis, ... }.
// `basis` is the human-readable explanation of what the forecast is based on.
// Rendered distinctly from a due date: a due date is a promise someone made,
// a forecast is what history says.
export const getTaskForecast = async (taskId) => {
  const { data, error } = await supabase.rpc("fn_forecast_task", { p_task_id: taskId });
  if (error) wrapSupabaseError(error, "fn_forecast_task");
  return wrapSupabase(data);
};

export const getWorkTakeaways = async (clientId) => {
  // Returns array of { panel, tint, says, drill } — insight surfaces.
  // warm = something leaking · cool = fixable · positive = good.
  const params = {};
  if (clientId) params.p_client_id = clientId;
  const { data, error } = await supabase.rpc("fn_work_takeaways", params);
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getWorkVerdict = async (clientId) => {
  // One sentence per person per day, generated overnight.
  // Returns { says, tint, focus, generated_at } or NULL.
  // Null means no fresh verdict — hide the block entirely.
  const { data, error } = await supabase.rpc("fn_verdict", {
    p_surface: "work",
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getSavedViews = async () => {
  // Returns saved filter views for the work surface.
  const { data, error } = await supabase.rpc("fn_saved_views", {
    p_surface: "work",
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

// ─── Work charts (manager-only) — backend views pending ─────────────────────────
// These wrap v_work_throughput / v_work_time_in_position / v_client_waiting_time.
// The views are not yet returning data (see work-functional-tracker.md Item 5 —
// "Blocked on backend views"). Wrappers are in place so WorkPage can wire useEffects
// the moment the views land; until then they are uncalled (no runtime cost / no errors).
export const getWorkThroughput = async (clientId) => {
  const { data, error } = await supabase.from("v_work_throughput").select("*");
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getWorkTimeInPosition = async (clientId) => {
  const { data, error } = await supabase.from("v_work_time_in_position").select("*");
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getClientWaitingTime = async (clientId) => {
  const { data, error } = await supabase.from("v_client_waiting_time").select("*");
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

// ─── Assignment suggestions ─────────────────────────────────────────────────────

export const getWhoCan = async (capability, clientId, limit = 6) => {
  // "Who can do this" by real capability, derived from 102k completed tasks.
  // Agents appear on identical terms.
  const params = { p_capability: capability, p_limit: limit };
  if (clientId) params.p_client_id = clientId;
  const { data, error } = await supabase.rpc("fn_who_can", params);
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getSuggestDoer = async (clientId, workType, skill, limit = 5) => {
  // Suggests who should DO the work. Each suggestion carries `why` in plain words.
  const params = { p_limit: limit };
  if (clientId)  params.p_client_id = clientId;
  if (workType)  params.p_work_type = workType;
  if (skill)     params.p_skill = skill;
  const { data, error } = await supabase.rpc("fn_suggest_doer", params);
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const getSuggestCommunicator = async (clientId, limit = 5) => {
  // Suggests who should TALK to the client. Different from doer — verified
  // on real data: 28 person-client pairs deliver without ever speaking to them.
  // p_client_id is ALWAYS sent (null when the task has no client) so the payload
  // matches the fn_suggest_communicator(p_client_id uuid, p_limit int) overload —
  // omitting it (the old conditional) produced PGRST202 on tasks without a client.
  const params = { p_client_id: clientId ?? null, p_limit: limit };
  const { data, error } = await supabase.rpc("fn_suggest_communicator", params);
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

// ─── Writes ─────────────────────────────────────────────────────────────────────

export const createWork = async ({
  title,
  work_type = "internal",
  client_id,
  status,
  description,
  owner_id,
  due,
  service,
  priority,
  thread_id,
  parent_task_id,
} = {}) => {
  // Anyone files, PMs triage. Leave status null — defaults to that pipeline's
  // first open position. Never guess one.
  const params = { p_work_type: work_type };
  if (title)           params.p_title = title;
  if (client_id)       params.p_client_id = client_id;
  if (status)          params.p_status = status;
  if (description)     params.p_description = description;
  if (owner_id)        params.p_owner_id = owner_id;
  if (due)             params.p_due = due;
  if (service)         params.p_service = service;
  if (priority)        params.p_priority = priority;
  if (thread_id)       params.p_thread_id = thread_id;
  if (parent_task_id)  params.p_parent_task_id = parent_task_id;
  const { data, error } = await supabase.rpc("fn_work_create", params);
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const moveWork = async (taskId, status) => {
  // Validates against the task's own pipeline. Raises with the valid list
  // if the status is wrong — show that message, it is useful.
  const { data, error } = await supabase.rpc("fn_work_move", {
    p_task_id: taskId,
    p_status: status,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const assignWork = async (taskId, ownerId) => {
  // Accepts agents. An agent without an accountable human owner is rejected.
  const { data, error } = await supabase.rpc("fn_work_assign", {
    p_task_id: taskId,
    p_owner_id: ownerId,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const updateWork = async ({
  task_id,
  title,
  description,
  due,
  clear_due = false,
  priority,
  service,
  tags,
  blocked_reason,
  client_visible,
  work_type,
  client_id,
} = {}) => {
  const params = { p_task_id: task_id, p_clear_due: clear_due };
  if (title)            params.p_title = title;
  if (description)      params.p_description = description;
  if (due)              params.p_due = due;
  if (priority)         params.p_priority = priority;
  if (service)          params.p_service = service;
  if (tags)             params.p_tags = tags;
  if (blocked_reason)   params.p_blocked_reason = blocked_reason;
  if (client_visible != null) params.p_client_visible = client_visible;
  // work_type / client_id — forwarded to fn_work_update as p_work_type /
  // p_client_id (the RPC param names documented in TaskHeader's work-type and
  // client-picker comments). These were MISSING from the destructure + params
  // build, so handleWorkTypeChange / handleClientChange in useWorkDetail passed
  // them in the payload object but updateWork silently dropped them — the RPC
  // ran with only p_task_id, so the change never reached the DB (the slide-out
  // showed the old value even after a refresh). work_type uses a truthy guard
  // (a non-empty code when the picker selects one). client_id is different: a
  // uuid sets it, null clears it (the slider's "Internal (no client)" option
  // passes null via handleClientChange), and undefined is dropped so callers
  // that omit it leave the client untouched.
  if (work_type)        params.p_work_type = work_type;
  if (client_id !== undefined) params.p_client_id = client_id;
  const { data, error } = await supabase.rpc("fn_work_update", params);
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const setWorkDone = async (taskId, done) => {
  // Toggles a task's done flag. `done: true` moves the task to its pipeline's
  // finished status and sets completed_at; `done: false` moves it back to the
  // pipeline's first open status and clears completed_at. Used by SubtaskSection
  // to toggle each subtask's dot — pass the subtask's own id, not the parent's.
  const { data, error } = await supabase.rpc("fn_work_set_done", {
    p_task_id: taskId,
    p_done: done,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const bulkWork = async (taskIds, action, value) => {
  // Applies the same functions in a loop — no fast path that skips validation.
  // Returns { ok, failed, errors[] }. Show the failures.
  const { data, error } = await supabase.rpc("fn_work_bulk", {
    p_task_ids: taskIds,
    p_action: action,
    p_value: value,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const deleteWork = async (taskId, reason) => {
  // SOFT delete. REFUSES when effort is logged or an open commitment points
  // at the work. Show the raised message and offer Archive instead.
  const params = { p_task_id: taskId };
  if (reason) params.p_reason = reason;
  const { data, error } = await supabase.rpc("fn_work_delete", params);
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const archiveWork = async (taskId) => {
  const { data, error } = await supabase.rpc("fn_work_archive", {
    p_task_id: taskId,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const restoreWork = async (taskId) => {
  // Undo. Toast with Undo after every delete.
  const { data, error } = await supabase.rpc("fn_work_restore", {
    p_task_id: taskId,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

// --- Recurring tasks -----------------------------------------------------
// Four RPC wrappers for the repeating-tasks feature (backend: Brian's
// 45_REPEATING_TASKS_SHEANE.md). All follow the same envelope pattern as the
// other task wrappers above — build p_ params, call supabase.rpc, raise via
// wrapSupabaseError on a Postgres error, return wrapSupabase(data) on success.

export const recurringFromTask = async (taskId, cadence, dayOfWeek, dayOfMonth) => {
  // Promotes an existing one-off task into a recurring rule. `cadence` is the
  // repeat unit (e.g. 'weekly', 'monthly'). `dayOfWeek` / `dayOfMonth` are only
  // meaningful for some cadences, so they default to null and are forwarded as
  // null when the caller omits them.
  const { data, error } = await supabase.rpc("fn_recurring_from_task", {
    p_task_id: taskId,
    p_cadence: cadence,
    p_day_of_week: dayOfWeek || null,
    p_day_of_month: dayOfMonth || null,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const recurringList = async (clientId) => {
  // Lists recurring rules. When `clientId` is omitted/null it returns ALL
  // recurring rules; pass a client id to scope to that client.
  const { data, error } = await supabase.rpc("fn_recurring_list", {
    p_client_id: clientId || null,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const recurringSetActive = async (id, active) => {
  // Pauses (`active: false`) or resumes (`active: true`) a recurring rule.
  // Returns the updated rule so the caller can refresh the row in place.
  const { data, error } = await supabase.rpc("fn_recurring_set_active", {
    p_id: id,
    p_active: active,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const recurringSave = async (rule) => {
  // Creates or updates a recurring rule. `rule` is the full rule object:
  //   id            — omit for a new rule, include to update an existing one
  //   title, description, work_type, client_id, owner_id
  //   cadence       — repeat unit (e.g. 'weekly', 'monthly')
  //   day_of_week, day_of_month — cadence-specific day selection
  //   lead_days, due_offset_days
  //   checklist     — array of steps, each { title, owner, due_offset_days }
  // Creating a new rule also makes its first copy immediately.
  const { data, error } = await supabase.rpc("fn_recurring_save", {
    p_rule: rule,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

// Supabase Storage bucket for task file attachments — the same bucket
// fn_work_attach records and fn_work_detach reports back in its `bucket`
// field. Module-level constant so the upload helper below is the only place
// that spells it out.
const WORK_ATTACHMENTS_BUCKET = 'work-attachments';

export const attachWork = async (taskId, { filename, storage_path, content_type, size_bytes, title }) => {
  // Step two of the attach flow: upload via uploadTaskFile first, then call
  // this with the resulting path.
  const { data, error } = await supabase.rpc("fn_work_attach", {
    p_task_id: taskId,
    p_filename: filename,
    p_storage_path: storage_path,
    p_content_type: content_type,
    p_size_bytes: size_bytes,
    p_title: title,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const detachWork = async (attachmentId) => {
  // Removes an attachment record. Returns { id, deleted, bucket, path,
  // remove_file } — when remove_file is true the caller must also delete the
  // Storage object: supabase.storage.from(data.bucket).remove([data.path]).
  const { data, error } = await supabase.rpc("fn_work_detach", {
    p_attachment_id: attachmentId,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const uploadTaskFile = async (path, file) => {
  // Step one of the attach flow: put the blob in Storage. The caller builds
  // the path (taskId/timestamp-safeName) and calls attachWork with this path
  // once the upload succeeds. Throws on error (same wrapSupabaseError pattern
  // as the RPC wrappers) so a failed upload never reaches fn_work_attach.
  const { data, error } = await supabase.storage
    .from(WORK_ATTACHMENTS_BUCKET)
    .upload(path, file, { contentType: file.type });
  if (error) wrapSupabaseError(error, 'work-attachments upload');
  return wrapSupabase(data);
};

export const upsertSavedView = async ({ name, filters, surface = "work", id, shared = false, _default = false }) => {
  const params = { p_name: name, p_filters: filters, p_surface: surface, p_shared: shared, p_default: _default };
  if (id) params.p_id = id;
  const { data, error } = await supabase.rpc("fn_saved_view_upsert", params);
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const deleteSavedView = async (viewId) => {
  // Raises on someone else's view.
  const { data, error } = await supabase.rpc("fn_saved_view_delete", {
    p_id: viewId,
  });
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};

export const sendWorkMessage = async ({
  task_id,
  body,
  channel,
  to,
  cc,
  subject,
  attachments,
  commitment_text,
  commitment_due,
  effort_minutes,
} = {}) => {
  // The ONLY message write path. Posts to that task's thread — the same store
  // as email, chat, DMs, and design feedback. There is no separate comment API.
  const params = { p_task_id: task_id };
  if (body)              params.p_body = body;
  if (channel)           params.p_channel = channel;
  if (to)                params.p_to = to;
  if (cc)                params.p_cc = cc;
  if (subject)           params.p_subject = subject;
  if (attachments)       params.p_attachments = attachments;
  if (commitment_text)   params.p_commitment_text = commitment_text;
  if (commitment_due)    params.p_commitment_due = commitment_due;
  if (effort_minutes)    params.p_effort_minutes = effort_minutes;
  const { data, error } = await supabase.rpc("fn_compose_send", params);
  if (error) wrapSupabaseError(error);
  return wrapSupabase(data);
};
